import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  IconButton,
  Tooltip,
  InputAdornment,
  Popover,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  History as HistoryIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Science as TestIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { api } from '../../services/api';

// Convert [text](url) markdown links → <a> tags for display in history
function renderBodyWithLinks(text) {
  const parts = [];
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  let lastIndex = 0;
  let match;
  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#2d5a42', fontWeight: 500 }}
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

// Toolbar + textarea body editor with Insert Link popover and personalization token buttons
function BodyEditor({ value, onChange, placeholder, rows = 10 }) {
  const textareaRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [selectionSnapshot, setSelectionSnapshot] = useState(null);

  const insertAtCursor = (token) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newValue = value.slice(0, start) + token + value.slice(end);
    onChange({ target: { value: newValue } });
    setTimeout(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleOpenLink = (e) => {
    const ta = textareaRef.current;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end);
    setSelectionSnapshot({ start, end });
    setLinkText(selected || '');
    setLinkUrl('');
    setAnchorEl(e.currentTarget);
  };

  const handleInsertLink = () => {
    const text = linkText.trim();
    const url = linkUrl.trim();
    if (!text || !url) return;

    const { start, end } = selectionSnapshot;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const inserted = `[${text}](${url})`;
    const newValue = before + inserted + after;
    onChange({ target: { value: newValue } });

    setAnchorEl(null);

    // Restore focus + move cursor to end of inserted text
    setTimeout(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        const pos = start + inserted.length;
        ta.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleInsertLink();
    }
    if (e.key === 'Escape') setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box>
      {/* Toolbar */}
      <Paper
        variant="outlined"
        sx={{
          borderBottom: 0,
          borderRadius: '4px 4px 0 0',
          px: 1,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: 'grey.50',
          flexWrap: 'wrap',
        }}
      >
        <Tooltip title="Insert link — select text first, then click">
          <IconButton size="small" onClick={handleOpenLink} sx={{ color: '#2d5a42' }}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', mx: 0.5 }} />
        <Tooltip title="Insert {{first_name}} — replaced with recipient's first name">
          <Button
            size="small"
            onClick={() => insertAtCursor('{{first_name}}')}
            sx={{ fontFamily: 'monospace', fontSize: '0.7rem', px: 0.75, py: 0.25, minWidth: 0, color: '#2d5a42', textTransform: 'none', lineHeight: 1.4 }}
          >
            {'{{first_name}}'}
          </Button>
        </Tooltip>
        <Tooltip title="Insert {{last_name}} — replaced with recipient's last name">
          <Button
            size="small"
            onClick={() => insertAtCursor('{{last_name}}')}
            sx={{ fontFamily: 'monospace', fontSize: '0.7rem', px: 0.75, py: 0.25, minWidth: 0, color: '#2d5a42', textTransform: 'none', lineHeight: 1.4 }}
          >
            {'{{last_name}}'}
          </Button>
        </Tooltip>
        <Tooltip title="Insert {{full_name}} — replaced with recipient's full name">
          <Button
            size="small"
            onClick={() => insertAtCursor('{{full_name}}')}
            sx={{ fontFamily: 'monospace', fontSize: '0.7rem', px: 0.75, py: 0.25, minWidth: 0, color: '#2d5a42', textTransform: 'none', lineHeight: 1.4 }}
          >
            {'{{full_name}}'}
          </Button>
        </Tooltip>
      </Paper>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '12px 14px',
          fontFamily: 'inherit',
          fontSize: '1rem',
          lineHeight: 1.6,
          border: '1px solid rgba(0,0,0,0.23)',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          resize: 'vertical',
          outline: 'none',
          color: '#333',
          background: '#fff',
        }}
        onFocus={e => { e.target.style.borderColor = '#2d5a42'; e.target.style.boxShadow = '0 0 0 1px #2d5a42'; }}
        onBlur={e => { e.target.style.borderColor = 'rgba(0,0,0,0.23)'; e.target.style.boxShadow = 'none'; }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Tip: Use {'{{first_name}}'}, {'{{last_name}}'}, or {'{{full_name}}'} to personalise each email. Links display as clickable anchors.
      </Typography>

      {/* Insert Link popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, width: 340 } }}
      >
        <Typography variant="subtitle2" gutterBottom>Insert Hyperlink</Typography>
        <Stack spacing={1.5}>
          <TextField
            label="Link text"
            size="small"
            fullWidth
            value={linkText}
            onChange={e => setLinkText(e.target.value)}
            autoFocus
            onKeyDown={handleKeyDown}
          />
          <TextField
            label="URL"
            size="small"
            fullWidth
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            placeholder="https://"
            onKeyDown={handleKeyDown}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setAnchorEl(null)}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleInsertLink}
              disabled={!linkText.trim() || !linkUrl.trim()}
              sx={{ bgcolor: '#2d5a42', '&:hover': { bgcolor: '#1e4034' } }}
            >
              Insert
            </Button>
          </Box>
        </Stack>
      </Popover>
    </Box>
  );
}

// Reusable chip-based email input box
function RecipientChipInput({ recipients, onAdd, onRemove, placeholder = 'Add email and press Enter...' }) {
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState('');
  const inputRef = useRef(null);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAdd = () => {
    const val = input.trim();
    if (!val) return;
    if (!isValidEmail(val)) {
      setInputError('Please enter a valid email address');
      return;
    }
    if (recipients.some(r => r.email === val)) {
      setInputError('This email is already in the list');
      return;
    }
    onAdd({ email: val, name: val });
    setInput('');
    setInputError('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          cursor: 'text',
          minHeight: 48,
        }}
        onClick={() => inputRef.current?.focus()}
      >
        {recipients.map((r) => (
          <Chip
            key={r.email}
            label={r.name && r.name !== r.email ? `${r.name} <${r.email}>` : r.email}
            size="small"
            onDelete={() => onRemove(r.email)}
            sx={{ bgcolor: '#e8f0eb', color: '#2d5a42', '& .MuiChip-deleteIcon': { color: '#2d5a42' } }}
          />
        ))}
        <TextField
          inputRef={inputRef}
          variant="standard"
          size="small"
          placeholder={recipients.length === 0 ? placeholder : 'Add more...'}
          value={input}
          onChange={(e) => { setInput(e.target.value); setInputError(''); }}
          onKeyDown={handleKeyDown}
          error={!!inputError}
          InputProps={{
            disableUnderline: true,
            endAdornment: input && (
              <InputAdornment position="end">
                <Tooltip title="Add email">
                  <IconButton size="small" onClick={handleAdd}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flexGrow: 1 }}
        />
      </Paper>
      {inputError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
          {inputError}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        Press Enter or comma to add • Click × to remove
      </Typography>
    </Box>
  );
}

function EmailHistoryRow({ entry }) {
  const [open, setOpen] = useState(false);

  const sentAt = new Date(entry.sent_at);
  const formattedDate = sentAt.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const formattedTime = sentAt.toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <Tooltip title={open ? 'Collapse' : 'Show recipients & body'}>
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">{entry.subject}</Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={`${entry.recipients.length} recipient${entry.recipients.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ bgcolor: '#e8f0eb', color: '#2d5a42' }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">{formattedDate}</Typography>
          <Typography variant="caption" color="text.secondary">{formattedTime}</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Recipients</Typography>
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {entry.recipients.map((r, i) => (
                  <Chip
                    key={i}
                    label={r.name && r.name !== r.email ? `${r.name} <${r.email}>` : r.email}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Typography variant="subtitle2" gutterBottom>Message</Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: 'grey.50', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
              >
                {renderBodyWithLinks(entry.body)}
              </Paper>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function EmailManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [emailType, setEmailType] = useState('all_parents');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [programs, setPrograms] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Email history state
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Test email state
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailAddresses, setTestEmailAddresses] = useState([]);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmailSuccess, setTestEmailSuccess] = useState('');
  const [testEmailError, setTestEmailError] = useState('');

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchHistory = useCallback(async (page, rowsPerPage) => {
    try {
      setIsLoadingHistory(true);
      setHistoryError('');
      const data = await api.getEmailHistory(rowsPerPage, page * rowsPerPage);
      setHistory(data.emails || []);
      setHistoryTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching email history:', err);
      setHistoryError('Failed to load email history');
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory(historyPage, historyRowsPerPage);
  }, [fetchHistory, historyPage, historyRowsPerPage]);

  useEffect(() => {
    setRecipients([]);
    setError('');
    if (emailType === 'all_parents') {
      fetchAllParents();
    } else if (emailType === 'program_parents' && selectedProgram) {
      fetchProgramParents(selectedProgram);
    }
  }, [emailType, selectedProgram]);

  const fetchPrograms = async () => {
    try {
      const data = await api.getPrograms();
      setPrograms(Array.isArray(data) ? data : (data.programs || []));
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  const fetchAllParents = async () => {
    try {
      setIsLoading(true);
      const data = await api.getUsersByRole('parent');
      const users = Array.isArray(data) ? data : (data.users || []);
      setRecipients(users
        .filter(u => u.email_verified && u.email)
        .map(u => ({
          email: u.email,
          name: `${u.first_name} ${u.last_name}`.trim() || u.email,
        })));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load parents');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProgramParents = async (programId) => {
    try {
      setIsLoading(true);
      const data = await api.getProgramEnrollments(programId);
      const parentMap = new Map();
      (data.enrollments || [])
        .filter(e => ['completed', 'paid'].includes(e.status?.toLowerCase()))
        .forEach(e => {
        if (e.enroller_email && !parentMap.has(e.enroller_email)) {
          parentMap.set(e.enroller_email, {
            email: e.enroller_email,
            name: e.enroller_name || e.enroller_email,
          });
        }
      });
      setRecipients(Array.from(parentMap.values()));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load program enrollments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRecipient = (r) => setRecipients(prev => [...prev, r]);
  const handleRemoveRecipient = (email) => setRecipients(prev => prev.filter(r => r.email !== email));

  const handleOpenTestEmail = () => {
    if (!testEmailOpen && testEmailAddresses.length === 0 && user?.email) {
      setTestEmailAddresses([{
        email: user.email,
        name: `${user.first_name} ${user.last_name}`.trim() || user.email,
      }]);
    }
    setTestEmailOpen(prev => !prev);
    setTestEmailSuccess('');
    setTestEmailError('');
  };

  const handleSendTestEmail = async () => {
    if (!subject.trim()) { setTestEmailError('Please enter a subject before sending a test'); return; }
    if (!body.trim()) { setTestEmailError('Please enter a message before sending a test'); return; }
    if (testEmailAddresses.length === 0) { setTestEmailError('Please add at least one email address'); return; }
    try {
      setIsSendingTest(true);
      setTestEmailError('');
      setTestEmailSuccess('');
      await api.sendGroupEmail(testEmailAddresses, `[TEST] ${subject}`, body);
      setTestEmailSuccess(`Test email sent to ${testEmailAddresses.map(r => r.email).join(', ')}`);
      fetchHistory(0, historyRowsPerPage);
      setHistoryPage(0);
    } catch (err) {
      setTestEmailError(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleSendEmail = async () => {
    if (!subject.trim()) { setError('Please enter a subject'); return; }
    if (!body.trim()) { setError('Please enter a message'); return; }
    if (recipients.length === 0) { setError('No recipients — add at least one address'); return; }
    try {
      setIsSending(true);
      setError('');
      setSuccess('');
      await api.sendGroupEmail(recipients, subject, body);
      setSuccess(`Successfully sent email to ${recipients.length} recipient(s)`);
      setSubject('');
      setBody('');
      fetchHistory(0, historyRowsPerPage);
      setHistoryPage(0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const totalRecipients = recipients.length;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="lg">
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>

        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Send Email to Parents
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Send personalized emails to parents. Use {'{{first_name}}'}, {'{{last_name}}'}, or {'{{full_name}}'} in your message to personalize each email.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ p: 3, mb: 4 }}>
          <Stack spacing={3}>

            {/* Group selector */}
            <FormControl fullWidth>
              <InputLabel>Select Recipient Group</InputLabel>
              <Select
                value={emailType}
                label="Select Recipient Group"
                onChange={(e) => setEmailType(e.target.value)}
              >
                <MenuItem value="all_parents">All Parents</MenuItem>
                <MenuItem value="program_parents">Parents of Enrolled Children (by Program)</MenuItem>
              </Select>
            </FormControl>

            {/* Program selector */}
            {emailType === 'program_parents' && (
              <FormControl fullWidth>
                <InputLabel>Select Program</InputLabel>
                <Select
                  value={selectedProgram}
                  label="Select Program"
                  onChange={(e) => setSelectedProgram(e.target.value)}
                >
                  {programs.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}{p.term ? ` - ${p.term}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Recipients chip list */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonAddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight="medium">
                  Recipients
                </Typography>
                {isLoading ? (
                  <CircularProgress size={14} sx={{ ml: 1 }} />
                ) : (
                  <Chip
                    label={totalRecipients}
                    size="small"
                    sx={{ height: 20, bgcolor: '#e8f0eb', color: '#2d5a42', fontWeight: 'bold', fontSize: '0.75rem' }}
                  />
                )}
              </Box>
              {emailType === 'all_parents' && !isLoading && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Only parents with verified email addresses are included automatically.
                </Typography>
              )}
              {isLoading ? (
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading recipients...</Typography>
                </Paper>
              ) : (
                <RecipientChipInput
                  recipients={recipients}
                  onAdd={handleAddRecipient}
                  onRemove={handleRemoveRecipient}
                  placeholder="Type an email and press Enter to add manually..."
                />
              )}
            </Box>

            {/* Subject */}
            <TextField
              fullWidth
              label="Email Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
            />

            {/* Body with link toolbar */}
            <BodyEditor
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={"Enter your message here. Use {{name}} to personalize with recipient's name."}
            />

            {/* Send Button */}
            <Button
              variant="contained"
              startIcon={isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSendEmail}
              disabled={isSending || totalRecipients === 0 || !subject.trim() || !body.trim()}
              sx={{ bgcolor: '#2d5a42', '&:hover': { bgcolor: '#1e4034' } }}
            >
              {isSending ? 'Sending...' : `Send Email to ${totalRecipients} Recipient${totalRecipients !== 1 ? 's' : ''}`}
            </Button>
          </Stack>
        </Paper>

        {/* Test Email Section */}
        <Paper sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { bgcolor: 'grey.50' },
              borderRadius: testEmailOpen ? '4px 4px 0 0' : '4px',
            }}
            onClick={handleOpenTestEmail}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TestIcon sx={{ color: '#2d5a42' }} />
              <Typography fontWeight="medium">Send Test Email</Typography>
              <Typography variant="body2" color="text.secondary">
                — preview how the email looks before sending to all recipients
              </Typography>
            </Box>
            <IconButton size="small">
              {testEmailOpen ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>

          <Collapse in={testEmailOpen}>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Stack spacing={2}>
                {testEmailError && (
                  <Alert severity="error" onClose={() => setTestEmailError('')}>{testEmailError}</Alert>
                )}
                {testEmailSuccess && (
                  <Alert severity="success" onClose={() => setTestEmailSuccess('')}>{testEmailSuccess}</Alert>
                )}
                <Typography variant="body2" color="text.secondary">
                  The test email will be sent with <strong>[TEST]</strong> prepended to the subject.
                  Personalization tokens ({'{{first_name}}'}, {'{{last_name}}'}, {'{{full_name}}'}) will be replaced with each recipient's name or email.
                </Typography>
                <Box>
                  <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                    Send test to:
                  </Typography>
                  <RecipientChipInput
                    recipients={testEmailAddresses}
                    onAdd={(r) => setTestEmailAddresses(prev => [...prev, r])}
                    onRemove={(email) => setTestEmailAddresses(prev => prev.filter(r => r.email !== email))}
                    placeholder="Add email and press Enter..."
                  />
                </Box>
                <Button
                  variant="outlined"
                  startIcon={isSendingTest ? <CircularProgress size={18} color="inherit" /> : <TestIcon />}
                  onClick={handleSendTestEmail}
                  disabled={isSendingTest || testEmailAddresses.length === 0 || !subject.trim() || !body.trim()}
                  sx={{ alignSelf: 'flex-start', borderColor: '#2d5a42', color: '#2d5a42', '&:hover': { borderColor: '#1e4034', bgcolor: '#f0f5f2' } }}
                >
                  {isSendingTest ? 'Sending Test...' : `Send Test to ${testEmailAddresses.length} Address${testEmailAddresses.length !== 1 ? 'es' : ''}`}
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </Paper>

        {/* Email History */}
        <Divider sx={{ mb: 4 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <HistoryIcon sx={{ color: '#2d5a42' }} />
          <Typography variant="h5" fontWeight="bold">Email History</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A record of all emails sent to parents. Click a row to view recipients and message body.
        </Typography>

        {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={48} />
                  <TableCell><Typography fontWeight="bold">Subject</Typography></TableCell>
                  <TableCell><Typography fontWeight="bold">Recipients</Typography></TableCell>
                  <TableCell><Typography fontWeight="bold">Sent</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingHistory ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No emails sent yet</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((entry) => (
                    <EmailHistoryRow key={entry.id} entry={entry} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={historyTotal}
            page={historyPage}
            onPageChange={(_, newPage) => setHistoryPage(newPage)}
            rowsPerPage={historyRowsPerPage}
            onRowsPerPageChange={(e) => {
              setHistoryRowsPerPage(parseInt(e.target.value, 10));
              setHistoryPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      </Container>
    </Box>
  );
}

export default EmailManagement;
